/**
 * Machines à valider — ce que les techniciens ont créé sur place (lot T4, registre B35) : liste à gauche, fiche à droite,
 * recherche en tête (même mise en page que « Suites à traiter », Le Parc et « Gammes »).
 *
 * Trois gestes : **Corriger** (le formulaire machine existant), **Valider** (la fiche devient une machine du parc comme une
 * autre ; la gamme se propose ensuite), **C'est une machine existante…** (choix d'une machine validée du même site ; la
 * confirmation dit ce qui bouge et ce qui reste). « Ressemble à » reprend la détection de doublons du parc.
 *
 * Décision de Benjamin (2026-10-27) : le bon d'une machine à valider se clôture et se valide normalement ; on corrige ici
 * ensuite. Les boutons dépendent des droits lus dans le jeton, MAIS seule l'API décide.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { ApiError, api, type Asset, type Doublon, type MachineAValider } from '../api/client';
import { useDroitsParc } from '../auth/useDroits';
import { AvertissementDoublons } from '../composants/AvertissementDoublons';
import { ErreurFormulaire, Panneau } from '../composants/Formulaire';
import { FormulaireMachine } from '../composants/FormulaireMachine';
import { PiecesJointes } from '../composants/PiecesJointes';
import { jourFr } from '../domaine/interventions';
import {
  bilanFusion,
  ciblesDeFusion,
  confirmationFusion,
  filtrerMachinesAValider,
  numeroSerieAffiche,
  titreMachineAValider,
} from '../domaine/machines-a-valider';

export function MachinesAValiderPage() {
  const [parametres, setParametres] = useSearchParams();
  const machineId = parametres.get('machine');
  const droits = useDroitsParc();

  const [recherche, setRecherche] = useState(parametres.get('q') ?? '');
  const [liste, setListe] = useState<readonly MachineAValider[] | null>(null);
  const [erreurListe, setErreurListe] = useState<string | null>(null);
  const [rechargement, setRechargement] = useState(0);

  const [fiche, setFiche] = useState<Asset | null>(null);
  const [ressemblances, setRessemblances] = useState<readonly Doublon[]>([]);
  const [corriger, setCorriger] = useState(false);
  const [fusion, setFusion] = useState<{ cibles: readonly Asset[]; cibleId: string } | null>(null);
  const [erreurAction, setErreurAction] = useState<ApiError | Error | null>(null);
  const [succes, setSucces] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const majUrl = useCallback(
    (changement: Record<string, string | null>) => {
      const p = new URLSearchParams(parametres);
      for (const [cle, valeur] of Object.entries(changement)) {
        if (valeur === null || valeur === '') p.delete(cle);
        else p.set(cle, valeur);
      }
      setParametres(p, { replace: true });
    },
    [parametres, setParametres],
  );

  useEffect(() => {
    let annule = false;
    api
      .machinesAValider()
      .then((l) => {
        if (annule) return;
        setListe(l.items);
        setErreurListe(null);
      })
      .catch((e: unknown) => {
        if (!annule) setErreurListe(e instanceof ApiError ? e.message : 'Chargement des machines à valider impossible.');
      });
    return () => {
      annule = true;
    };
  }, [rechargement]);

  const visibles = useMemo(() => filtrerMachinesAValider(liste ?? [], recherche), [liste, recherche]);
  const choisie = visibles.find((m) => m.id === machineId) ?? null;

  useEffect(() => {
    const premiere = visibles[0];
    if (premiere && !choisie) majUrl({ machine: premiere.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibles, choisie]);

  // La fiche complète (pour « Corriger ») et ses ressemblances (la détection de doublons du parc).
  useEffect(() => {
    if (!choisie) {
      setFiche(null);
      setRessemblances([]);
      return;
    }
    let annule = false;
    setFiche(null);
    api
      .asset(choisie.id)
      .then((a) => {
        if (!annule) setFiche(a);
      })
      .catch(() => undefined);
    api
      .doublonsMachine({
        serialNumber: choisie.numeroSerie ?? undefined,
        brandPartyId: choisie.marque?.id,
        model: choisie.modele ?? undefined,
        siteId: choisie.site?.id,
        clientDesignation: choisie.nomClient ?? undefined,
        excludeId: choisie.id,
      })
      .then((r) => {
        if (!annule) setRessemblances(r.doublons);
      })
      .catch(() => {
        if (!annule) setRessemblances([]);
      });
    return () => {
      annule = true;
    };
  }, [choisie]);

  const rafraichir = () => setRechargement((n) => n + 1);

  const selectionner = (id: string) => {
    setSucces(null);
    setErreurAction(null);
    majUrl({ machine: id });
  };

  const valider = async () => {
    if (!choisie) return;
    setEnCours(true);
    try {
      await api.validerMachine(choisie.id, fiche?.revision ?? choisie.revision);
      setErreurAction(null);
      setSucces(`« ${titreMachineAValider(choisie)} » est validée : elle est au parc comme une autre machine.`);
      majUrl({ machine: null });
      rafraichir();
    } catch (e) {
      setSucces(null);
      setErreurAction(e instanceof ApiError || e instanceof Error ? e : new Error('Validation impossible.'));
      rafraichir();
    } finally {
      setEnCours(false);
    }
  };

  const ouvrirFusion = async () => {
    if (!choisie?.site) return;
    setErreurAction(null);
    try {
      const duSite = await api.assets({ siteId: choisie.site.id, take: 500 });
      setFusion({ cibles: ciblesDeFusion(choisie, duSite.items), cibleId: '' });
    } catch (e) {
      setErreurAction(e instanceof ApiError || e instanceof Error ? e : new Error('Lecture des machines du site impossible.'));
    }
  };

  const fusionner = async () => {
    if (!choisie || !fusion || fusion.cibleId === '') return;
    setEnCours(true);
    try {
      const r = await api.fusionnerMachine(choisie.id, fusion.cibleId, fiche?.revision ?? choisie.revision);
      setFusion(null);
      setErreurAction(null);
      setSucces(bilanFusion(r));
      majUrl({ machine: null });
      rafraichir();
    } catch (e) {
      setErreurAction(e instanceof ApiError || e instanceof Error ? e : new Error('Fusion impossible.'));
      setFusion(null);
      rafraichir();
    } finally {
      setEnCours(false);
    }
  };

  const messages = (
    <>
      {succes && <div className="message succes">{succes}</div>}
      <ErreurFormulaire message={erreurAction?.message ?? null} details={erreurAction instanceof ApiError ? erreurAction.details : undefined} />
    </>
  );
  const cibleChoisie = fusion?.cibles.find((c) => c.id === fusion.cibleId) ?? null;

  return (
    <div className="gammes-page">
      <div className="entete-page">
        <div>
          <h1>Machines à valider</h1>
          <p>
            Créées sur place par les techniciens, sur le site de leur intervention. Corrigez-les, validez-les, ou dites que c’est une
            machine existante. <Link to="/parc">Retour au Parc</Link>
          </p>
        </div>
      </div>

      {!droits.peutGererMachines && (
        <div className="message info">Vous pouvez lire ces fiches ; les valider ou les fusionner demande le droit de gérer les machines.</div>
      )}

      <div className="gammes-mise-en-page">
        <div className="carte gammes-liste">
          <div className="gammes-liste-entete">
            <input
              type="text"
              autoFocus
              aria-label="Rechercher une machine à valider"
              placeholder="Rechercher : nature, marque, n° de série, site, client, bon…"
              value={recherche}
              onChange={(e) => {
                setRecherche(e.target.value);
                majUrl({ q: e.target.value.trim(), machine: null });
              }}
            />
            <div className="gammes-liste-outils">
              <span className="aide">
                {liste === null ? 'Chargement…' : `${visibles.length} machine${visibles.length > 1 ? 's' : ''} à valider`}
              </span>
            </div>
          </div>
          <div className="gammes-liste-corps">
            {erreurListe && <div className="message erreur">{erreurListe}</div>}
            {liste !== null && visibles.length === 0 && (
              <div className="vide">
                {recherche.trim() !== '' ? 'Aucune machine à valider ne correspond.' : 'Aucune machine à valider : tout est en ordre.'}
              </div>
            )}
            <ul className="parc-lignes">
              {visibles.map((m) => {
                const serie = numeroSerieAffiche(m.numeroSerie);
                return (
                  <li key={m.id}>
                    <button
                      type="button"
                      className={`parc-ligne${m.id === machineId ? ' actif' : ''}`}
                      aria-pressed={m.id === machineId}
                      onClick={() => selectionner(m.id)}
                    >
                      <div className="parc-ligne-badges">
                        <span className="badge">{m.nature}</span>
                        <span className="etat attente">à valider</span>
                      </div>
                      <div className="parc-ligne-titre">
                        <strong>{titreMachineAValider(m)}</strong>
                      </div>
                      <div className="aide">
                        n° <span className={serie.illisible ? 'etat attente' : ''}>{serie.texte}</span>
                        {m.nomClient ? ` · « ${m.nomClient} »` : ''}
                      </div>
                      <div className="aide">{[m.client?.nom, m.site?.nom].filter(Boolean).join(' · ') || 'Sans site'}</div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        <div className="gammes-fiche">
          {!choisie && (succes || erreurAction) && <div className="gammes-fiche-entete">{messages}</div>}
          {choisie && (
            <>
              <div className="gammes-fiche-entete">
                <div className="gammes-fiche-titre">
                  <h2>
                    {titreMachineAValider(choisie)} <span className="etat attente">à valider</span>
                  </h2>
                  {droits.peutGererMachines && (
                    <div className="saisie-ligne">
                      <button className="secondaire" disabled={enCours || fiche === null} onClick={() => setCorriger(true)}>
                        Corriger
                      </button>
                      <button className="secondaire" disabled={enCours || choisie.site === null} onClick={() => void ouvrirFusion()}>
                        C’est une machine existante…
                      </button>
                      <button disabled={enCours} onClick={() => void valider()}>
                        Valider
                      </button>
                    </div>
                  )}
                </div>
                {messages}
              </div>

              <div className="carte">
                <div className="carte-corps">
                  <dl className="proprietes">
                    <dt>Nature</dt>
                    <dd>{choisie.nature}</dd>
                    <dt>Marque</dt>
                    <dd>{choisie.marque?.nom ?? <span className="aide">—</span>}</dd>
                    <dt>Modèle</dt>
                    <dd>{choisie.modele ?? <span className="aide">—</span>}</dd>
                    <dt>N° de série</dt>
                    <dd>
                      {numeroSerieAffiche(choisie.numeroSerie).illisible ? (
                        <span className="etat attente">illisible</span>
                      ) : (
                        choisie.numeroSerie
                      )}
                    </dd>
                    <dt>Nom chez le client</dt>
                    <dd>{choisie.nomClient ?? <span className="aide">—</span>}</dd>
                    <dt>Site</dt>
                    <dd>
                      {choisie.site ? <Link to={`/sites/${choisie.site.id}`}>{choisie.site.nom}</Link> : <span className="aide">—</span>}
                      {choisie.client ? ` · ${choisie.client.nom}` : ''}
                    </dd>
                    <dt>Créée par</dt>
                    <dd>{choisie.creePar ?? <span className="aide">—</span>}</dd>
                    <dt>Le</dt>
                    <dd>{jourFr(choisie.creeLe.slice(0, 10))}</dd>
                    <dt>Bon d’origine</dt>
                    <dd>
                      {choisie.bonOrigine ? (
                        <Link to={`/interventions?bon=${choisie.bonOrigine.id}`}>{choisie.bonOrigine.numero ?? 'sans numéro'}</Link>
                      ) : (
                        <span className="aide">—</span>
                      )}
                    </dd>
                    <dt>Étiquette</dt>
                    <dd>{choisie.qrCode}</dd>
                  </dl>
                </div>
              </div>

              <div className="carte">
                <h2>Ressemble à</h2>
                <div className="carte-corps">
                  {ressemblances.length === 0 ? (
                    <p className="aide">Aucune fiche du parc ne lui ressemble (même marque et même n° de série, ou même nom chez le client sur ce site).</p>
                  ) : (
                    <AvertissementDoublons objet="machine" doublons={ressemblances} />
                  )}
                </div>
              </div>

              <div className="carte">
                <h2>Photo{choisie.photos > 1 ? 's' : ''} de la plaque</h2>
                <div className="carte-corps">
                  {choisie.photos === 0 && <p className="aide">Aucune photo prise sur place (elle est facultative).</p>}
                  <PiecesJointes ownerType="asset" ownerId={choisie.id} />
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {corriger && fiche && (
        <FormulaireMachine
          machine={fiche}
          onFermer={() => setCorriger(false)}
          onEnregistre={(m) => {
            setCorriger(false);
            setFiche(m);
            setSucces('Fiche corrigée. Validez-la quand elle est juste.');
            rafraichir();
          }}
        />
      )}

      {fusion && choisie && (
        <Panneau
          titre="C’est une machine existante"
          sousTitre={`Choisissez la machine du site ${choisie.site?.nom ?? ''} qu’elle est en réalité.`}
          onFermer={() => setFusion(null)}
          actions={
            <>
              <button type="button" className="secondaire" disabled={enCours} onClick={() => setFusion(null)}>
                Ne rien faire
              </button>
              <button type="button" className="danger" disabled={enCours || cibleChoisie === null} onClick={() => void fusionner()}>
                Fusionner
              </button>
            </>
          }
        >
          {fusion.cibles.length === 0 ? (
            <p className="aide">Ce site n’a aucune autre machine validée : validez plutôt cette fiche.</p>
          ) : (
            <div className="champ">
              <label htmlFor="cible-fusion">Machine existante</label>
              <select id="cible-fusion" value={fusion.cibleId} onChange={(e) => setFusion({ ...fusion, cibleId: e.target.value })}>
                <option value="">Choisir…</option>
                {fusion.cibles.map((c) => (
                  <option key={c.id} value={c.id}>
                    {[c.label ?? c.model ?? 'Sans désignation', c.serialNumber ? `n° ${c.serialNumber}` : null, c.clientDesignation ? `« ${c.clientDesignation} »` : null]
                      .filter(Boolean)
                      .join(' · ')}
                  </option>
                ))}
              </select>
            </div>
          )}
          {cibleChoisie && <p className="message info">{confirmationFusion(choisie, cibleChoisie)}</p>}
        </Panneau>
      )}
    </div>
  );
}
